import { useParams, Navigate, Link } from "react-router-dom";
import { Clock, User, ArrowUpRight } from "lucide-react";
import Seo, { faqSchema, breadcrumbSchema } from "@/components/Seo";
import { Breadcrumbs } from "@/components/shared/PageHero";
import FaqAccordion from "@/components/shared/FaqAccordion";
import CtaSection from "@/components/shared/CtaSection";
import { Reveal } from "@/components/motion/Reveal";
import { getPost, POSTS, AUTHORS } from "@/data/blog";

function Block({ block }) {
  if (block.type === "h2") return <h2 className="font-display text-2xl lg:text-3xl font-bold tracking-tight mt-12 mb-4">{block.text}</h2>;
  if (block.type === "list")
    return (
      <ul className="my-6 space-y-3">
        {block.items.map((it, i) => (
          <li key={i} className="flex items-start gap-3 text-gray-700 leading-relaxed"><span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#0B63F6] shrink-0" /> {it}</li>
        ))}
      </ul>
    );
  return <p className="text-lg text-gray-700 leading-relaxed my-5">{block.text}</p>;
}

export default function BlogPost() {
  const { slug } = useParams();
  const post = getPost(slug);
  if (!post) return <Navigate to="/blog" replace />;
  const author = AUTHORS[post.author];
  const related = POSTS.filter((p) => p.slug !== slug).slice(0, 2);
  const toc = post.content.filter((b) => b.type === "h2").map((b) => b.text);

  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    headline: post.title,
    description: post.excerpt,
    author: { "@type": "Organization", name: author.name },
    publisher: { "@type": "Organization", name: "CompuBrain AI", logo: { "@type": "ImageObject", url: "https://compubrain.ai" } },
    datePublished: post.date,
    mainEntityOfPage: `https://compubrain.ai/blog/${slug}`,
  };

  return (
    <>
      <Seo
        title={post.title}
        description={post.excerpt}
        path={`/blog/${slug}`}
        type="article"
        schemas={[articleSchema, faqSchema(post.faqs || []), breadcrumbSchema([{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }, { name: post.title, path: `/blog/${slug}` }])]}
      />
      <article className="pt-32 lg:pt-40">
        <div className="max-w-3xl mx-auto px-6">
          <Breadcrumbs items={[{ name: "Home", path: "/" }, { name: "Blog", path: "/blog" }, { name: post.category }]} />
          <span className="inline-block mt-6 text-xs font-bold uppercase tracking-[0.2em] text-[#0B63F6]">{post.category}</span>
          <h1 className="font-display text-4xl lg:text-5xl font-extrabold tracking-tightest leading-[1.05] mt-4">{post.title}</h1>
          <div className="flex items-center gap-5 mt-6 text-sm text-gray-500">
            <span className="flex items-center gap-2"><User className="h-4 w-4" /> {author.name}</span>
            <span className="flex items-center gap-2"><Clock className="h-4 w-4" /> {post.readingTime} min read</span>
            <span>{new Date(post.date).toLocaleDateString("en-US", { year: "numeric", month: "long", day: "numeric" })}</span>
          </div>
        </div>

        {toc.length > 0 && (
          <div className="max-w-3xl mx-auto px-6 mt-10">
            <div className="rounded-2xl border border-gray-200 bg-[#FAFAFA] p-6">
              <div className="text-xs font-bold uppercase tracking-[0.2em] text-gray-500 mb-3">On this page</div>
              <ul className="space-y-2">
                {toc.map((t, i) => <li key={i} className="text-[15px] text-gray-700">{i + 1}. {t}</li>)}
              </ul>
            </div>
          </div>
        )}

        <div className="max-w-3xl mx-auto px-6 mt-8">
          {post.content.map((b, i) => <Block key={i} block={b} />)}
        </div>

        {post.faqs?.length > 0 && (
          <div className="max-w-3xl mx-auto px-6 mt-16">
            <h2 className="font-display text-2xl font-bold tracking-tight mb-4">Frequently asked questions</h2>
            <FaqAccordion faqs={post.faqs} />
          </div>
        )}

        <div className="max-w-3xl mx-auto px-6 mt-16">
          <div className="rounded-2xl border border-gray-200 p-6 flex items-start gap-4">
            <span className="h-12 w-12 rounded-full bg-[#0B63F6]/[0.1] flex items-center justify-center text-[#0B63F6] font-display font-bold shrink-0">CB</span>
            <div>
              <div className="font-display font-bold">{author.name}</div>
              <div className="text-sm text-gray-500">{author.role}</div>
              <p className="text-[15px] text-gray-600 mt-2 leading-relaxed">{author.bio}</p>
            </div>
          </div>
        </div>

        <div className="max-w-3xl mx-auto px-6 mt-16">
          <h2 className="font-display text-2xl font-bold tracking-tight mb-6">Related articles</h2>
          <div className="grid sm:grid-cols-2 gap-5">
            {related.map((r) => (
              <Link key={r.slug} to={`/blog/${r.slug}`} className="group rounded-2xl border border-gray-200 p-6 hover:border-gray-300 transition-all">
                <span className="text-xs font-bold uppercase tracking-[0.15em] text-[#0B63F6]">{r.category}</span>
                <h3 className="font-display text-lg font-bold tracking-tight mt-3">{r.title}</h3>
                <span className="mt-4 inline-flex items-center gap-1 text-sm text-[#0B63F6]">Read <ArrowUpRight className="h-4 w-4" /></span>
              </Link>
            ))}
          </div>
        </div>
      </article>
      <div className="mt-24"><CtaSection heading="Want results, not just reading?" sub="Let CompuBrain AI implement GEO for your brand." /></div>
    </>
  );
}
